import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Gift, ExternalLink, Copy, Check, ChevronRight } from 'lucide-react';

const SEGMENTS = [
  { label: '25 Free Spins', subLabel: 'No Deposit Required', icon: '\u{1F3B0}', color: '#4d7cfe' },
  { label: 'Better Luck', subLabel: 'Next Time', icon: '\u{1F605}', color: '#2d3348' },
  { label: '50 Bonus Spins', subLabel: 'On First Deposit', icon: '\u2728', color: '#8b5cf6' },
];

const toRad = (deg) => (deg * Math.PI) / 180;

/* Wheel uses conic-gradient: from -60deg centers Segment 1 at top (0°) */
const WHEEL_GRADIENT = `conic-gradient(from -60deg,
  #4d7cfe 0deg, #4d7cfe 119.4deg,
  rgba(255,255,255,0.45) 119.4deg, rgba(255,255,255,0.45) 120.6deg,
  #2d3348 120.6deg, #2d3348 239.4deg,
  rgba(255,255,255,0.45) 239.4deg, rgba(255,255,255,0.45) 240.6deg,
  #8b5cf6 240.6deg, #8b5cf6 360deg
)`;

/* Segment label positions (relative %, from center, at each segment's midpoint) */
const LABEL_POSITIONS = [
  { angle: 0, x: 50, y: 24 },     // Segment 1: top
  { angle: 120, x: 72.5, y: 63 }, // Segment 2: lower-right
  { angle: 240, x: 27.5, y: 63 }, // Segment 3: lower-left
];

function Confetti() {
  const particles = useMemo(() =>
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2.5 + Math.random() * 2,
      color: ['#4d7cfe', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'][i % 6],
      size: 5 + Math.random() * 9,
      isCircle: Math.random() > 0.5,
      drift: (Math.random() - 0.5) * 120,
    })),
  []);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map(p => (
        <div key={p.id} className="absolute" style={{
          left: `${p.left}%`, top: -20,
          width: p.size, height: p.size,
          backgroundColor: p.color,
          borderRadius: p.isCircle ? '50%' : '2px',
          animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
          '--drift': `${p.drift}px`,
        }} />
      ))}
    </div>
  );
}

export default function SpinToWin() {
  const [spinning, setSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(() => sessionStorage.getItem('ciq_spun') === '1');
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSpin = async () => {
    if (spinning || hasSpun) return;
    setSpinning(true);
    setResult(null);

    try {
      await api.post('/tools/spin-wheel');

      // 4-7 full rotations, always lands on Segment 1 (centered at 0° due to gradient offset)
      const fullRotations = Math.floor(Math.random() * 4) + 4;
      const jitter = Math.floor(Math.random() * 24) - 12;
      setRotation(prev => prev + fullRotations * 360 + jitter);

      setTimeout(() => {
        setResult({ prize: '25 Free Spins', subtext: 'No Deposit Required', code: 'SPINZ' });
        setShowConfetti(true);
        setSpinning(false);
        setHasSpun(true);
        sessionStorage.setItem('ciq_spun', '1');
        setTimeout(() => setShowConfetti(false), 3000);
      }, 5200);
    } catch {
      toast.error('Spin failed');
      setSpinning(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText('SPINZ').then(() => {
      setCopied(true);
      toast.success('Code copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-8 pb-12" data-testid="spin-to-win-page">
      {showConfetti && <Confetti />}

      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) translateX(var(--drift, 0px)) rotate(720deg); opacity: 0; }
        }
      `}</style>

      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6" data-testid="spin-breadcrumb">
        <Link to="/dashboard" className="hover:text-[#4d7cfe] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Viral Tools</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-white">Spin to Win</span>
      </nav>

      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-[#f59e0b]/15 flex items-center justify-center mx-auto mb-4">
          <Gift className="h-7 w-7 text-[#f59e0b]" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2" data-testid="spin-heading">Spin to Win</h1>
        <p className="text-[#a0a8b8] text-sm">Spin the wheel to unlock exclusive casino bonus codes</p>
      </div>

      {/* Already spun message */}
      {hasSpun && !result ? (
        <div className="text-center py-12" data-testid="already-spun-msg">
          <div className="w-16 h-16 rounded-full bg-[#f59e0b]/10 flex items-center justify-center mx-auto mb-4">
            <Gift className="h-8 w-8 text-[#f59e0b]" />
          </div>
          <p className="text-white font-semibold mb-2">You've already claimed your free spin today!</p>
          <p className="text-[#a0a8b8] text-sm">Come back tomorrow for another spin</p>
        </div>
      ) : (
        <>
          {/* Wheel */}
          <div className="relative w-[300px] h-[300px] md:w-[400px] md:h-[400px] mx-auto mb-8" data-testid="wheel-container">
            {/* Pointer */}
            <div className="absolute top-[-2px] left-1/2 -translate-x-1/2 z-30" data-testid="wheel-pointer"
              style={{
                width: 0, height: 0,
                borderLeft: '14px solid transparent',
                borderRight: '14px solid transparent',
                borderTop: '28px solid #f59e0b',
                filter: 'drop-shadow(0 3px 6px rgba(245,158,11,0.5))',
              }} />

            {/* Glow */}
            <div className="absolute inset-[-10px] rounded-full pointer-events-none"
              style={{ boxShadow: '0 0 40px rgba(77,124,254,0.4), 0 0 80px rgba(77,124,254,0.15)' }} />

            {/* Rotating wheel */}
            <div
              className="w-full h-full relative z-10"
              data-testid="wheel-rotator"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: spinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
              }}
            >
              {/* Wheel circle with conic-gradient segments */}
              <div className="w-full h-full rounded-full relative overflow-hidden"
                style={{
                  background: WHEEL_GRADIENT,
                  boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)',
                  border: '4px solid rgba(255,255,255,0.15)',
                }}
                data-testid="wheel-face"
              >
                {/* Outer gold ring */}
                <div className="absolute inset-[-2px] rounded-full pointer-events-none"
                  style={{ border: '2px solid rgba(245,158,11,0.3)' }} />

                {/* Segment labels */}
                {SEGMENTS.map((seg, i) => {
                  const lp = LABEL_POSITIONS[i];
                  return (
                    <div key={i} className="absolute text-center pointer-events-none"
                      style={{
                        left: `${lp.x}%`, top: `${lp.y}%`,
                        transform: `translate(-50%, -50%) rotate(${lp.angle}deg)`,
                        width: '110px',
                      }}
                      data-testid={`segment-label-${i}`}
                    >
                      <div className="text-2xl mb-1" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>{seg.icon}</div>
                      <p className="text-white font-bold text-[13px] leading-tight"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}>{seg.label}</p>
                      <p className="text-white/60 text-[10px] mt-0.5"
                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{seg.subLabel}</p>
                    </div>
                  );
                })}

                {/* Divider lines from center to edge */}
                {[-60, 60, 180].map(deg => (
                  <div key={deg} className="absolute top-1/2 left-1/2 origin-left pointer-events-none"
                    style={{
                      width: '50%', height: '2px',
                      background: 'rgba(255,255,255,0.4)',
                      transform: `rotate(${deg - 90}deg)`,
                    }} />
                ))}

                {/* Center hub */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#1a1f2e] border-[3px] border-[#4d7cfe]/60 flex flex-col items-center justify-center z-10"
                  style={{ boxShadow: '0 0 12px rgba(77,124,254,0.3)' }}
                  data-testid="wheel-hub">
                  <span className="text-white text-[7px] md:text-[8px] font-extrabold tracking-wider">CASINO</span>
                  <span className="text-[#4d7cfe] text-[7px] md:text-[8px] font-extrabold tracking-wider">IQ</span>
                </div>
              </div>
            </div>
          </div>

          {/* Spin Button */}
          {!result && (
            <button
              onClick={handleSpin}
              disabled={spinning || hasSpun}
              className="w-full max-w-sm mx-auto h-14 rounded-xl bg-[#4d7cfe] text-white font-bold text-lg flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:brightness-100"
              data-testid="spin-btn"
            >
              {spinning ? 'Spinning...' : 'SPIN NOW'}
            </button>
          )}

          {/* Result Screen */}
          {result && (
            <div className="animate-slide-up max-w-md mx-auto" data-testid="win-result">
              <div className="text-center mb-6">
                <p className="text-5xl mb-3">{'\u{1F389}'}</p>
                <h2 className="text-2xl font-bold text-white mb-1" data-testid="win-headline">You Won!</h2>
                <p className="text-lg text-[#4d7cfe] font-semibold">{result.prize} — {result.subtext}</p>
              </div>

              {/* Promo Code */}
              <div className="flex items-center justify-between gap-3 mb-4"
                style={{ padding: '16px 24px', background: 'rgba(255,255,255,0.04)', border: '1px dashed #4d7cfe', borderRadius: 8 }}
                data-testid="promo-code-box">
                <div>
                  <p className="text-[11px] text-[#64748b] uppercase tracking-wider mb-1">Your Code</p>
                  <p className="text-2xl font-bold text-white" style={{ letterSpacing: '4px' }} data-testid="prize-code">SPINZ</p>
                </div>
                <button onClick={handleCopyCode}
                  className="h-10 px-4 rounded-lg bg-[#4d7cfe]/10 border border-[#4d7cfe]/30 text-[#4d7cfe] text-sm font-medium flex items-center gap-1.5 hover:bg-[#4d7cfe]/20 transition-colors shrink-0"
                  data-testid="copy-code-btn">
                  {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy Code</>}
                </button>
              </div>

              <p className="text-xs text-[#64748b] text-center mb-5 leading-relaxed">
                Applies to BitStarz Casino only. Enter code <strong className="text-white">SPINZ</strong> on registration. No deposit required to claim.
              </p>

              <a href="https://www.bitstarz.com/?ref=CASINOIQ" target="_blank" rel="noopener noreferrer"
                className="w-full h-14 rounded-xl bg-[#4d7cfe] text-white font-bold text-lg flex items-center justify-center gap-2 transition-all hover:brightness-110 hover:scale-[1.02]"
                data-testid="claim-bitstarz-btn">
                Claim at BitStarz <ExternalLink className="h-5 w-5" />
              </a>

              <p className="text-[11px] text-[#64748b] text-center mt-4 leading-relaxed" data-testid="spin-terms">
                25 Free Spins valid for new players only. No deposit required. Winnings subject to 40x wagering requirement. Expires 7 days after claim.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
